/* eslint-disable no-console */
import { setHeaderVersion } from '../util/getHeaderVersion';
import type { HeaderVersion } from '../../core/types';

declare global {
    interface Window {
        dataLayer?: unknown[];
    }
}

enum ExperimentName {
    header = 'header',
    checkout = 'checkout',
}

const experimentHandlers: Record<
    ExperimentName,
    (variant: unknown) => void | null
> = {
    [ExperimentName.header]: variant =>
        setHeaderVersion(variant as HeaderVersion),
    [ExperimentName.checkout]: () => void 0,
};

interface ExperimentConfig {
    experimentName: ExperimentName;
    variants: string[];
    sampleRate: number;
}

export class GoogleAnalyticsService {
    constructor() {
        this.init();
    }

    private getExperiments(): ExperimentConfig[] {
        try {
            return APP_ENV.AB_EXPERIMENTS
                ? JSON.parse(APP_ENV.AB_EXPERIMENTS)
                : [];
        } catch (e) {
            return [];
        }
    }

    protected getStoredVariant(experimentName: string): string | null {
        try {
            return localStorage.getItem(experimentName);
        } catch (e) {
            return null;
        }
    }

    private storeVariant(experimentName: string, variant: string): void {
        try {
            localStorage.setItem(experimentName, variant);
        } catch {
            void 0;
        }
    }

    private pickRandomVariant(variants: string[]): string {
        return variants[Math.floor(Math.random() * variants.length)];
    }

    private oldVariant(sampleRate: number): boolean {
        return Math.random() * 100 < sampleRate;
    }

    private fetchVariant(
        experimentName: string,
        variants: string[],
        sampleRate: number
    ): string | null {
        const stored = this.getStoredVariant(experimentName);
        if (stored) {
            return stored;
        }

        if (!sampleRate || sampleRate === 0) {
            return null;
        }

        if (this.oldVariant(sampleRate)) {
            return variants[0];
        }

        return this.pickRandomVariant(variants.slice(1));
    }

    private commitVariant(
        experimentName: ExperimentName,
        variant: string
    ): void {
        this.storeVariant(experimentName, variant);
        this.trackEvent('set_experiment_variant', {
            experiment_name: experimentName,
            experiment_variant: variant,
        });
    }

    init(): this {
        const experiments = this.getExperiments();

        for (const { experimentName, variants, sampleRate } of experiments) {
            const variant = this.fetchVariant(
                experimentName,
                variants,
                sampleRate
            );

            if (variant) {
                this.commitVariant(experimentName, variant);
                experimentHandlers[experimentName](variant as HeaderVersion);
            }
        }

        return this;
    }

    trackEvent(eventName: string, params?: Record<string, unknown>): void {
        window.dataLayer = window.dataLayer || [];

        if (params?.ecommerce) {
            window.dataLayer.push({ ecommerce: null });
        }

        window.dataLayer.push({
            event: eventName,
            ...params,
        });
    }

    isInHeaderExperiment() {
        return !!this.getStoredVariant('header');
    }
}

export default new GoogleAnalyticsService();
